import { defineStore } from 'pinia'
import { ref } from 'vue'

type SelectionState = Record<number, number | null>

const STORAGE_KEY = 'calculation-environment-database-selection'

const loadSelectionState = (): SelectionState => {
  if (typeof window === 'undefined') return {}
  const raw = window.localStorage.getItem(STORAGE_KEY)
  if (!raw) return {}

  try {
    const parsed = JSON.parse(raw) as SelectionState
    if (!parsed || typeof parsed !== 'object') return {}
    return parsed
  } catch {
    return {}
  }
}

export const useCalculationEnvironmentDatabaseSelectionStore = defineStore(
  'calculationEnvironmentDatabaseSelection',
  () => {
    const selectedByCalculation = ref<SelectionState>(loadSelectionState())

    const persist = () => {
      if (typeof window === 'undefined') return
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedByCalculation.value))
    }

    const getSelectedDatabase = (calculationId: number): number | null => {
      return selectedByCalculation.value[calculationId] ?? null
    }

    const setSelectedDatabase = (calculationId: number, databaseId: number | null) => {
      selectedByCalculation.value[calculationId] = databaseId
      persist()
    }

    const clearSelectedDatabase = (calculationId: number) => {
      delete selectedByCalculation.value[calculationId]
      persist()
    }

    const clearInvalidSelectedDatabase = (calculationId: number, validDatabaseIds: number[]) => {
      const selectedDatabaseId = getSelectedDatabase(calculationId)
      if (selectedDatabaseId === null) return false
      if (validDatabaseIds.includes(selectedDatabaseId)) return false
      clearSelectedDatabase(calculationId)
      return true
    }

    return {
      selectedByCalculation,
      getSelectedDatabase,
      setSelectedDatabase,
      clearSelectedDatabase,
      clearInvalidSelectedDatabase
    }
  }
)
